import React, { FunctionComponent, useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  SxProps,
  Stack,
  Typography,
  Button,
  Chip,
} from '@mui/material';
import moment from 'moment';

import { IScheduleShortEvent } from '../../../../store/V2/ScheduleSlice';
import {
  getClassShortUrlByWaitingId,
  getSubjectColorNew,
} from '../../../../helper';

interface UpcomingClassCardProps {
  classData: IScheduleShortEvent;
}

const containerStyles: SxProps = {
  borderRadius: '12px',
  border: '1px solid black',
  transition: 'all 0.3s ease-in-out',
};

const actionButtonStyles: SxProps = {
  padding: { xs: '4px 10px', sm: '8px 20px' },
  borderRadius: '25px',
  fontWeight: 600,
  fontSize: '12px',
  '&:hover': {
    backgroundColor: '#ffffff',
    color: '#1E293B',
  },
  minWidth: '100px',
  transition: 'transform 0.3s ease-in-out',
};

const chipStyles: SxProps = {
  borderRadius: '5px',
  padding: '8px',
  fontSize: '10px',
  fontWeight: 600,
  height: 'unset',
  width: 'fit-content',
  '& .MuiChip-label': {
    padding: 0,
  },
};

const UpcomingClassCard: FunctionComponent<UpcomingClassCardProps> = ({
  classData,
}) => {
  // State
  const [timeToStart, setTimeToStart] = useState('');
  const [showJoinButton, setShowJoinButton] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const subjectColor = classData.subject
    ? getSubjectColorNew(classData.subject)
    : '#1C9FF7';

  const classLink = classData.waitingId
    ? getClassShortUrlByWaitingId(classData.waitingId)
    : '';

  useEffect(() => {
    let intervalId;

    const classStartTime = moment(classData.start);
    const updateCountdown = () => {
      const now = moment();
      const diffInHours = classStartTime.diff(now, 'minutes', true);

      if (diffInHours < 10) {
        setShowJoinButton(true);
        clearInterval(intervalId);
      } else {
        setShowJoinButton(false);
        setTimeToStart(classStartTime.fromNow());
      }
    };

    updateCountdown();

    intervalId = setInterval(updateCountdown, 1000);

    return () => clearInterval(intervalId);
  }, []);

  return (
    <div style={{ padding: '0 10px' }}>
      <Card
        elevation={0}
        sx={{
          ...containerStyles,
          borderColor: subjectColor,
          backgroundColor:
            showJoinButton && isHovered ? subjectColor : '#ffffff',
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <CardContent
          style={{
            padding: '16px',
            position: 'relative',
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack gap="8px">
              <Typography
                fontSize="14px"
                fontWeight={600}
                color={subjectColor}
                className="ellipsis-text one-line"
              >
                {classData.subject}
              </Typography>
              <Chip
                label={classData.type}
                sx={{
                  ...chipStyles,
                  backgroundColor: `${subjectColor}1A`,
                  color: subjectColor,
                }}
              />
            </Stack>
            {showJoinButton ? (
              <Button
                disableElevation
                target="_blank"
                size="small"
                variant="contained"
                color="inherit"
                href={classLink}
                sx={{
                  ...actionButtonStyles,
                  transform: isHovered ? 'translate(-50%, -50%)' : 'none',
                  left: isHovered ? '50%' : 'unset',
                  top: isHovered ? '50%' : 'unset',
                  position: isHovered ? 'absolute' : 'unset',
                  color: isHovered ? '#1E293B' : '#ffffff',
                  backgroundColor: isHovered ? '#ffffff' : '#1E293B',
                }}
              >
                Join Now
              </Button>
            ) : (
              <Typography fontSize="12px" fontWeight={600} color={subjectColor}>
                {timeToStart}
              </Typography>
            )}
          </Stack>
        </CardContent>
      </Card>
    </div>
  );
};

export default UpcomingClassCard;
